import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  Bus, ArrowLeft, CreditCard, Smartphone, Banknote, 
  CheckCircle, Clock, MapPin, User, Ticket, QrCode, AlertCircle, Loader2
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import api from '../../utils/api';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';

// Initialize Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_emergent');

// Card Element styling
const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': {
        color: '#aab7c4',
      },
      padding: '12px',
    },
    invalid: {
      color: '#9e2146',
    },
  },
  hidePostalCode: true,
};

// Card Payment Form Component
const CardPaymentForm = ({ clientSecret, onSuccess, bookingData, amount }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError('');

    try {
      // First create the booking
      const bookingResponse = await api.post('/bus/bookings', bookingData);
      const bookingId = bookingResponse.data.booking.id;

      // Confirm card payment
      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
        },
      });

      if (stripeError) {
        setError(stripeError.message);
        setProcessing(false);
        return;
      }

      if (paymentIntent.status === 'succeeded') {
        // Confirm payment on backend
        await api.post('/bus/payments/confirm', {
          booking_id: bookingId,
          payment_intent_id: paymentIntent.id
        });

        // Update booking with paid status
        const updatedBooking = {
          ...bookingResponse.data.booking,
          paymentStatus: 'paid'
        };
        
        onSuccess(updatedBooking);
        
        toast({
          title: 'Payment Successful!',
          description: 'Your bus ticket has been booked and paid',
        });
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError(err.response?.data?.detail || 'Payment failed. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="p-4 border border-gray-300 rounded-lg bg-white">
        <CardElement options={cardElementOptions} />
      </div>
      
      {error && (
        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}
      
      <Button
        type="submit"
        disabled={!stripe || processing}
        className="w-full mt-4 bg-blue-600 hover:bg-blue-700"
        data-testid="pay-with-card-btn"
      >
        {processing ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Processing...
          </>
        ) : (
          <>
            <CreditCard className="h-4 w-4 mr-2" />
            Pay K{amount} Now
          </>
        )}
      </Button>
      
      <p className="mt-2 text-xs text-center text-gray-500">
        Your card will be charged K{amount}
      </p>
    </form>
  );
};

const BusCheckout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { trip, selectedSeats, passengerDetails, extraLuggage, pricing, selectedDestination } = location.state || {};
  
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [booking, setBooking] = useState(null);
  
  // Mobile Money state
  const [mobileMoneyProvider, setMobileMoneyProvider] = useState('mtn'); // 'mtn' or 'airtel'
  const [mobileMoneyPhone, setMobileMoneyPhone] = useState('');
  
  // Card payment state
  const [clientSecret, setClientSecret] = useState('');
  const [cardPaymentLoading, setCardPaymentLoading] = useState(false);
  const [cardError, setCardError] = useState('');

  // Determine the alighting city and station
  const alightingCity = selectedDestination?.city || trip?.route?.destination?.city;
  const alightingStation = selectedDestination?.station || trip?.route?.destination?.station || 'Main Station';
  const isIntermediateStop = selectedDestination && !selectedDestination.isFinalDestination;

  useEffect(() => {
    // Guest checkout allowed — no login required to book bus tickets
    if (!trip || !selectedSeats || !passengerDetails) {
      navigate('/bus');
    }
    
    // Pre-fill mobile money phone from first passenger
    if (passengerDetails?.[0]?.phone) {
      setMobileMoneyPhone(passengerDetails[0].phone);
    }
  }, [trip, selectedSeats, passengerDetails, navigate]);

  // Create payment intent when card payment is selected
  const createPaymentIntent = async () => {
    if (!pricing?.total) return;
    
    setCardPaymentLoading(true);
    setCardError('');
    
    try {
      const response = await api.post('/bus/payments/create-intent', {
        amount: pricing.total
      });
      
      if (response.data.success) {
        setClientSecret(response.data.clientSecret);
      } else {
        setCardError('Failed to initialize payment');
      }
    } catch (error) {
      console.error('Payment intent error:', error);
      setCardError(error.response?.data?.detail || 'Failed to initialize card payment');
    } finally {
      setCardPaymentLoading(false);
    }
  };

  // Handle payment method change
  const handlePaymentMethodChange = (methodId) => {
    setPaymentMethod(methodId);
    setCardError('');
    
    // Create payment intent when card is selected
    if (methodId === 'card' && !clientSecret) {
      createPaymentIntent();
    }
  };

  const handleBooking = async () => {
    // Validate mobile money details if selected
    if (paymentMethod === 'mobile_money') {
      if (!mobileMoneyPhone || mobileMoneyPhone.length < 10) {
        toast({
          title: 'Phone Number Required',
          description: 'Please enter a valid phone number for Mobile Money payment',
          variant: 'destructive'
        });
        return;
      }
    }
    
    setLoading(true);
    
    try {
      const response = await api.post('/bus/bookings', {
        trip_id: trip.tripId,
        passengers: passengerDetails.map(p => ({
          name: p.name,
          phone: p.phone,
          id_number: p.id_number || null,
          seat: p.seat,
          seat_class: p.seat_class
        })),
        boarding_station: trip.route?.origin?.station || 'Main Station',
        boarding_city: trip.route?.origin?.city,
        alighting_station: alightingStation,
        alighting_city: alightingCity,
        payment_method: paymentMethod,
        extra_luggage_kg: extraLuggage || 0,
        // Mobile money details
        mobile_money_provider: paymentMethod === 'mobile_money' ? mobileMoneyProvider : null,
        mobile_money_phone: paymentMethod === 'mobile_money' ? mobileMoneyPhone : null
      });

      setBooking(response.data.booking);
      
      toast({
        title: 'Booking Successful!',
        description: 'Your bus ticket has been booked',
      });
    } catch (error) {
      console.error('Booking error:', error);
      toast({
        title: 'Booking Failed',
        description: error.response?.data?.detail || 'Failed to complete booking',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const paymentMethods = [
    { id: 'cash', name: 'Pay at Station', icon: Banknote, description: 'Pay cash when boarding' },
    { id: 'mobile_money', name: 'Mobile Money', icon: Smartphone, description: 'Pay with MTN/Airtel' },
    { id: 'card', name: 'Card Payment', icon: CreditCard, description: 'Visa/Mastercard' },
  ];

  if (!trip) {
    return null;
  }

  // Booking Confirmation View
  if (booking) {
    const isPending = booking.paymentStatus !== 'paid';
    const isCashPayment = booking.paymentMethod === 'cash';
    const isMobilePayment = booking.paymentMethod === 'mobile_money';
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 pb-20">
        <div className={`${isPending ? 'bg-gradient-to-r from-amber-500 to-orange-500' : 'bg-gradient-to-r from-green-500 to-emerald-500'} text-white p-6`}>
          <div className="max-w-4xl mx-auto text-center">
            {isPending ? (
              <Clock className="h-16 w-16 mx-auto mb-4" />
            ) : (
              <CheckCircle className="h-16 w-16 mx-auto mb-4" />
            )}
            <h1 className="text-2xl font-bold mb-2">
              {isPending ? 'Booking Reserved!' : 'Booking Confirmed!'}
            </h1>
            <p className={isPending ? 'text-amber-100' : 'text-green-100'}>
              {isCashPayment 
                ? 'Your seat is reserved - Pay at the station before departure'
                : isMobilePayment
                  ? 'Your seat is reserved - Complete payment via Mobile Money'
                  : 'Your bus ticket has been booked successfully'}
            </p>
          </div>
        </div>

        <div className="max-w-2xl mx-auto p-4 -mt-4">
          {/* Payment Instructions Banner for Pending Payments */}
          {isPending && (
            <Card className="p-4 mb-4 bg-amber-50 border-amber-200">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-amber-800">Payment Required</h3>
                  {isCashPayment ? (
                    <p className="text-sm text-amber-700 mt-1">
                      Please arrive at least 30 minutes before departure to complete payment at the station counter. 
                      Your reservation will be cancelled if payment is not made before departure.
                    </p>
                  ) : isMobilePayment ? (
                    <p className="text-sm text-amber-700 mt-1">
                      Complete your payment using MTN MoMo or Airtel Money. Your ticket will be confirmed once payment is received.
                      <br /><strong>Amount: K{booking.pricing?.total || pricing?.total}</strong>
                    </p>
                  ) : (
                    <p className="text-sm text-amber-700 mt-1">
                      Please complete your payment to confirm your booking.
                    </p>
                  )}
                </div>
              </div>
            </Card>
          )}

          <Card className="p-6" data-testid="booking-confirmation">
            {/* Ticket Code */}
            <div className="text-center mb-6 pb-6 border-b border-dashed">
              <Ticket className="h-8 w-8 text-blue-600 mx-auto mb-2" />
              <p className="text-sm text-gray-500">Ticket Code</p>
              <p className="text-3xl font-bold text-blue-600 tracking-wider">
                {booking.ticketCode}
              </p>
            </div>

            {/* QR Code */}
            {booking.qrCode && (
              <div className="text-center mb-6">
                <img 
                  src={booking.qrCode} 
                  alt="Ticket QR Code" 
                  className="mx-auto w-40 h-40"
                />
                <p className="text-sm text-gray-500 mt-2">Show this QR code when boarding</p>
              </div>
            )}

            {/* Trip Details */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="text-center">
                  <p className="text-2xl font-bold">{trip.departureTime}</p>
                  <p className="text-sm text-gray-500">{booking.boarding?.city}</p>
                </div>
                <div className="flex-1 flex items-center gap-2">
                  <div className="flex-1 border-t-2 border-dashed border-gray-300" />
                  <Bus className="h-6 w-6 text-blue-600" />
                  <div className="flex-1 border-t-2 border-dashed border-gray-300" />
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold">{trip.arrivalTime}</p>
                  <p className="text-sm text-gray-500">{booking.alighting?.city}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Date</p>
                  <p className="font-medium">{trip.date || booking.trip?.date}</p>
                </div>
                <div>
                  <p className="text-gray-500">Operator</p>
                  <p className="font-medium">{trip.operatorName}</p>
                </div>
              </div>
            </div>

            {/* Passengers */}
            <div className="mb-6">
              <h3 className="font-semibold mb-3">Passengers</h3>
              <div className="space-y-2">
                {booking.passengers?.map((passenger, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-blue-600" />
                      <span className="font-medium">{passenger.name}</span>
                    </div>
                    <span className="bg-blue-600 text-white px-3 py-1 rounded text-sm font-medium">
                      Seat {passenger.seat}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Payment Summary */}
            <div className="border-t pt-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-gray-500">Payment Status</span>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  booking.paymentStatus === 'paid' 
                    ? 'bg-green-100 text-green-700'
                    : 'bg-amber-100 text-amber-700'
                }`}>
                  {booking.paymentStatus === 'paid' ? 'Paid' : 'Payment Pending'}
                </span>
              </div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-gray-500">Payment Method</span>
                <span className="font-medium">
                  {booking.paymentMethod === 'cash' 
                    ? 'Pay at Station' 
                    : booking.paymentMethod === 'mobile_money'
                      ? 'Mobile Money'
                      : 'Card Payment'}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="font-semibold">
                  {booking.paymentStatus === 'paid' ? 'Amount Paid' : 'Amount Due'}
                </span>
                <span className={`text-2xl font-bold ${
                  booking.paymentStatus === 'paid' ? 'text-green-600' : 'text-amber-600'
                }`}>
                  K{booking.pricing?.total || pricing?.total}
                </span>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button 
                variant="outline"
                className="flex-1"
                onClick={() => navigate('/bus/tickets')}
              >
                View My Tickets
              </Button>
              <Button 
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                onClick={() => navigate('/bus')}
              >
                Book Another Trip
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)}>
              <ArrowLeft className="h-6 w-6" />
            </button>
            <div>
              <h1 className="text-xl font-bold">Checkout</h1>
              <p className="text-blue-100 text-sm">Complete your booking</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-4">
        {/* Trip Summary */}
        <Card className="p-4 mb-4" data-testid="trip-summary">
          <h3 className="font-semibold mb-3">Trip Summary</h3>
          <div className="flex items-center gap-4 mb-4">
            <div className="text-center">
              <p className="text-xl font-bold">{trip.departureTime}</p>
              <p className="text-sm text-gray-500">{trip.route?.origin?.city}</p>
            </div>
            <div className="flex-1 flex items-center gap-2">
              <div className="flex-1 border-t-2 border-dashed border-gray-300" />
              <Bus className="h-5 w-5 text-blue-600" />
              <div className="flex-1 border-t-2 border-dashed border-gray-300" />
            </div>
            <div className="text-center">
              <p className="text-xl font-bold">{trip.arrivalTime}</p>
              <p className="text-sm text-gray-500">{alightingCity}</p>
              {isIntermediateStop && (
                <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">
                  Intermediate
                </span>
              )}
            </div>
          </div>
          
          <div className="flex flex-wrap gap-4 text-sm text-gray-500">
            <span className="flex items-center gap-1">
              <Clock className="h-4 w-4" />
              {trip.operatorName}
            </span>
            <span>
              {selectedSeats.length} seat(s): {selectedSeats.map(s => s.seatId).join(', ')}
            </span>
          </div>
        </Card>

        {/* Passengers */}
        <Card className="p-4 mb-4">
          <h3 className="font-semibold mb-3">Passengers</h3>
          <div className="space-y-2">
            {passengerDetails.map((p, idx) => (
              <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-gray-400" />
                  <span>{p.name}</span>
                </div>
                <span className="text-sm bg-blue-100 text-blue-600 px-2 py-0.5 rounded">
                  Seat {p.seat}
                </span>
              </div>
            ))}
          </div>
        </Card>

        {/* Payment Method */}
        <Card className="p-4 mb-4" data-testid="payment-method-selection">
          <h3 className="font-semibold mb-3">Payment Method</h3>
          <div className="space-y-2">
            {paymentMethods.map((method) => (
              <button
                key={method.id}
                onClick={() => handlePaymentMethodChange(method.id)}
                className={`w-full p-4 rounded-lg border-2 flex items-center gap-4 transition-all ${
                  paymentMethod === method.id
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-blue-200'
                }`}
                data-testid={`payment-${method.id}`}
              >
                <method.icon className={`h-6 w-6 ${
                  paymentMethod === method.id ? 'text-blue-600' : 'text-gray-400'
                }`} />
                <div className="text-left">
                  <p className="font-medium">{method.name}</p>
                  <p className="text-sm text-gray-500">{method.description}</p>
                </div>
                {paymentMethod === method.id && (
                  <CheckCircle className="h-5 w-5 text-blue-600 ml-auto" />
                )}
              </button>
            ))}
          </div>
          
          {/* Card Payment Input */}
          {paymentMethod === 'card' && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200" data-testid="card-payment-details">
              <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-blue-600" />
                Enter Card Details
              </h4>
              
              {cardPaymentLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                  <span className="ml-2 text-gray-500">Initializing secure payment...</span>
                </div>
              ) : cardError ? (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
                    <div>
                      <p className="text-amber-800 font-medium">Card Payment Unavailable</p>
                      <p className="text-amber-700 text-sm mt-1">{cardError}</p>
                      <div className="mt-3 flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handlePaymentMethodChange('mobile_money')}
                          className="border-amber-300 text-amber-700 hover:bg-amber-100"
                        >
                          <Smartphone className="h-4 w-4 mr-1" />
                          Use Mobile Money
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handlePaymentMethodChange('cash')}
                          className="border-amber-300 text-amber-700 hover:bg-amber-100"
                        >
                          <Banknote className="h-4 w-4 mr-1" />
                          Pay at Station
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : clientSecret ? (
                <Elements stripe={stripePromise} options={{ clientSecret }}>
                  <CardPaymentForm 
                    clientSecret={clientSecret}
                    onSuccess={(booking) => setBooking(booking)}
                    bookingData={{
                      trip_id: trip.tripId,
                      passengers: passengerDetails.map(p => ({
                        name: p.name,
                        phone: p.phone,
                        id_number: p.id_number || null,
                        seat: p.seat,
                        seat_class: p.seat_class
                      })),
                      boarding_station: trip.route?.origin?.station || 'Main Station',
                      boarding_city: trip.route?.origin?.city,
                      alighting_station: alightingStation,
                      alighting_city: alightingCity,
                      payment_method: 'card',
                      extra_luggage_kg: extraLuggage || 0
                    }}
                    amount={pricing?.total}
                  />
                </Elements>
              ) : (
                <div className="text-center py-4 text-gray-500">
                  Click to initialize card payment
                </div>
              )}
              
              {/* Secure payment badge */}
              <div className="mt-4 flex items-center justify-center gap-2 text-sm text-gray-500">
                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                </svg>
                Secured by Stripe
              </div>
            </div>
          )}
          
          {/* Mobile Money Provider Selection */}
          {paymentMethod === 'mobile_money' && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200" data-testid="mobile-money-details">
              <h4 className="font-medium text-gray-900 mb-3">Select Mobile Money Provider</h4>
              
              {/* Provider Selection */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                <button
                  onClick={() => setMobileMoneyProvider('mtn')}
                  className={`p-3 rounded-lg border-2 flex flex-col items-center gap-2 transition-all ${
                    mobileMoneyProvider === 'mtn'
                      ? 'border-yellow-500 bg-yellow-50'
                      : 'border-gray-200 hover:border-yellow-300'
                  }`}
                  data-testid="mtn-provider-btn"
                >
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    mobileMoneyProvider === 'mtn' ? 'bg-yellow-500' : 'bg-yellow-100'
                  }`}>
                    <span className={`font-bold text-lg ${
                      mobileMoneyProvider === 'mtn' ? 'text-white' : 'text-yellow-600'
                    }`}>MTN</span>
                  </div>
                  <span className="font-medium text-sm">MTN MoMo</span>
                  {mobileMoneyProvider === 'mtn' && (
                    <CheckCircle className="h-4 w-4 text-yellow-500" />
                  )}
                </button>
                
                <button
                  onClick={() => setMobileMoneyProvider('airtel')}
                  className={`p-3 rounded-lg border-2 flex flex-col items-center gap-2 transition-all ${
                    mobileMoneyProvider === 'airtel'
                      ? 'border-red-500 bg-red-50'
                      : 'border-gray-200 hover:border-red-300'
                  }`}
                  data-testid="airtel-provider-btn"
                >
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    mobileMoneyProvider === 'airtel' ? 'bg-red-500' : 'bg-red-100'
                  }`}>
                    <span className={`font-bold text-sm ${
                      mobileMoneyProvider === 'airtel' ? 'text-white' : 'text-red-600'
                    }`}>Airtel</span>
                  </div>
                  <span className="font-medium text-sm">Airtel Money</span>
                  {mobileMoneyProvider === 'airtel' && (
                    <CheckCircle className="h-4 w-4 text-red-500" />
                  )}
                </button>
              </div>
              
              {/* Phone Number Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {mobileMoneyProvider === 'mtn' ? 'MTN MoMo' : 'Airtel Money'} Phone Number
                </label>
                <div className="relative">
                  <Smartphone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="tel"
                    value={mobileMoneyPhone}
                    onChange={(e) => setMobileMoneyPhone(e.target.value)}
                    placeholder="+260 XXX XXX XXX"
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    data-testid="mobile-money-phone-input"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  You will receive a payment prompt on this number
                </p>
              </div>
              
              {/* Payment Instructions */}
              <div className={`mt-3 p-3 rounded-lg ${
                mobileMoneyProvider === 'mtn' ? 'bg-yellow-50 border border-yellow-200' : 'bg-red-50 border border-red-200'
              }`}>
                <p className={`text-sm ${mobileMoneyProvider === 'mtn' ? 'text-yellow-800' : 'text-red-800'}`}>
                  <strong>How it works:</strong> After clicking "Confirm Booking", you'll receive a payment prompt on your {mobileMoneyProvider === 'mtn' ? 'MTN MoMo' : 'Airtel Money'} app. Enter your PIN to complete the payment of <strong>K{pricing?.total}</strong>.
                </p>
              </div>
            </div>
          )}
        </Card>

        {/* Price Summary */}
        <Card className="p-4 mb-4">
          <h3 className="font-semibold mb-3">Price Summary</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span>Seat Subtotal ({selectedSeats.length} seats)</span>
              <span>K{pricing.subtotal}</span>
            </div>
            {pricing.luggageFee > 0 && (
              <div className="flex justify-between">
                <span>Extra Luggage ({extraLuggage} kg)</span>
                <span>K{pricing.luggageFee}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span>Processing fee {pricing.seatCount > 1 && pricing.platformFeePerSeat ? `(K${pricing.platformFeePerSeat} × ${pricing.seatCount})` : ''}</span>
              <span data-testid="checkout-platform-fee">K{pricing.platformFee ?? pricing.serviceFee}</span>
            </div>
            <div className="flex justify-between font-bold text-lg border-t pt-2">
              <span>Total</span>
              <span className="text-blue-600">K{pricing.total}</span>
            </div>
          </div>
        </Card>

        {/* Book Button - Hide for card payment since it has its own button */}
        {paymentMethod !== 'card' && (
          <Button 
            className="w-full bg-blue-600 hover:bg-blue-700 py-6 text-lg"
            onClick={handleBooking}
            disabled={loading}
            data-testid="confirm-booking-btn"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                Processing...
              </>
            ) : (
              <>
                <Ticket className="h-5 w-5 mr-2" />
                Confirm Booking - K{pricing.total}
              </>
            )}
          </Button>
        )}

        <p className="text-center text-sm text-gray-500 mt-4">
          By confirming, you agree to our terms and conditions
        </p>
      </div>
    </div>
  );
};

export default BusCheckout;
